import ExcellentExport, { ConvertOptions } from 'excellentexport'
import { CSSProperties, useCallback, useEffect, useMemo, useState } from 'react'
import DataGrid, { CellClickArgs, Column, SortColumn, SortDirection } from 'react-data-grid'
import { Arr } from '../../../helpers/Arr'
import { useSettings } from '../../../hooks/useSettings'
import { Filter } from './Filter/Filter'
import { FilterModal } from './FilterModal/FilterModal'
import './Grid.scss'
import { GridOptions } from './GridOptions/GridOptions'
import { Pagination } from './Pagination/Pagination'

export interface GridColumn<T> extends Column<T, Record<string, string[] | undefined>> {
  /** Column label displayed in column settings modal. */
  label?: string
  filterBy?: (item: T) => string | number | undefined
  sortBy?: (item: T) => string | number | Date | undefined
  exportFormat?: (item: T) => string | undefined | null
  hide?: boolean
  remove?: boolean
  filterItems?: string[]
  filter?: string[]
  sortDirection?: SortDirection
  parentKey?: string
}

export interface GridProps<T> {
  id: string
  title: string
  icon: string
  columns: GridColumn<T>[]
  rows: T[]
  style?: CSSProperties
  rowClass?: (row: T, index: number) => string
  rowKeyGetter: (row: T) => string
  onCellDoubleClick?: (data: CellClickArgs<T, Record<string, string[] | undefined>>) => void
  onReload?: () => void
}

export function Grid<T>(props: GridProps<T>) {
  const [rowsPerPage, setRowsPerPage] = useState(25)
  const [currentPage, setCurrentPage] = useState(0)
  const [filtersVisible, setFiltersVisible] = useState(true)
  const [columns, setColumns] = useState<GridColumn<T>[]>([])
  const [filterModalColumn, setFilterModalColumn] = useState<GridColumn<T>>()
  const [lastChangeTimestamp, setLastChangeTimestamp] = useState(0)
  const [selectedRowKey, setSelectedRowKey] = useState<string>()
  const [settings, updateSettings] = useSettings('iLinkGrids')
  const settingsLoaded = settings[props.id] != undefined

  const handleFilterClick = useCallback((column: GridColumn<T>) => {
    if (column.filterItems?.length) {
      setFilterModalColumn(column)
    }
  }, [])

  useEffect(() => {
    const validColumns = props.columns.filter((c) => !c.remove)
    const columnMap = Arr.toObject(
      validColumns,
      (c) => c.key,
      (c) => ({ ...c })
    )
    let columnKeys = validColumns.map((c) => c.key)
    const gridSettings = settings[props.id]
    if (gridSettings) {
      setRowsPerPage(gridSettings.rowsPerPage)
      setFiltersVisible(gridSettings.filtersVisible)
      let match = true
      gridSettings.columns.forEach((c) => {
        if (columnMap[c.key]) {
          columnMap[c.key]!.filter = c.filter
          columnMap[c.key]!.hide = c.hide
          columnMap[c.key]!.sortDirection = c.sortDirection
        } else {
          match = false
        }
      })
      if (match && columnKeys.length === gridSettings.columns.length) {
        columnKeys = gridSettings.columns.map((c) => c.key)
      }
    }
    const cols = columnKeys.map((key) => {
      const column = columnMap[key]!
      column.sortable = !!column.sortBy
      column.renderSummaryCell = (data) => <Filter data={data} onChange={updateFilter} onClick={handleFilterClick} />
      return column
    })
    setColumns(cols)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.columns, props.id, handleFilterClick, settingsLoaded])

  useEffect(() => {
    if (lastChangeTimestamp !== 0) {
      updateSettings({
        ...settings,
        [props.id]: {
          filtersVisible,
          rowsPerPage,
          columns: columns.map((c) => ({ key: c.key, filter: c.filter, hide: c.hide, sortDirection: c.sortDirection })),
        },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastChangeTimestamp])

  const updateFilter = (key: string, value: string[] | undefined) => {
    setColumns((current) => {
      const idx = current.findIndex((c) => c.key === key)
      const updated = [...current]
      updated[idx] = { ...updated[idx], filter: value }
      return updated
    })
    setLastChangeTimestamp(new Date().getTime())
  }

  const updateSortColumns = (sortColumns: SortColumn[]) => {
    setColumns((current) => {
      const updated = current.map((c) => ({
        ...c,
        sortDirection: sortColumns.slice(-1).find((sc) => sc.columnKey === c.key)?.direction,
      }))
      return updated
    })
    setLastChangeTimestamp(new Date().getTime())
  }

  const updateRowsPerPage = (count: number) => {
    setRowsPerPage(count)
    setLastChangeTimestamp(new Date().getTime())
  }

  const updateFilersVisible = (visible: boolean) => {
    setFiltersVisible(visible)
    setLastChangeTimestamp(new Date().getTime())
  }

  const updateColumns = (columns: GridColumn<T>[]) => {
    setColumns(columns)
    setLastChangeTimestamp(new Date().getTime())
  }

  const filters = useMemo(() => {
    return Arr.toObject(
      columns,
      (c) => c.key,
      (c) => c.filter
    )
  }, [columns])

  const sortColumns = useMemo(() => {
    return columns.filter((c) => c.sortDirection).map((c) => ({ columnKey: c.key, direction: c.sortDirection! }))
  }, [columns])

  const renderedColumns = useMemo(() => {
    return columns.filter((c) => !c.hide)
  }, [columns])

  const sortedFilteredRows = useMemo(() => {
    let result: T[] = []
    const filterColumns = columns.filter((c) => c.filter?.length)
    for (const row of props.rows) {
      let match = true
      for (const c of filterColumns) {
        const value = c.filterBy ? c.filterBy(row) : row[c.key as keyof T]
        if (!c.filter!.some((f) => !f || value?.toString().toLowerCase().includes(f.toLowerCase()))) {
          match = false
          continue
        }
      }
      if (match) {
        result.push(row)
      }
    }

    const sortColumn = columns.find((c) => c.sortDirection && c.sortBy)
    if (sortColumn) {
      result = sort(result, sortColumn.sortDirection!, sortColumn.sortBy!)
    }
    return result
  }, [props.rows, columns])

  const pageCount = useMemo(() => {
    if (rowsPerPage < 1 || !sortedFilteredRows.length) {
      return 1
    }
    return Math.ceil(sortedFilteredRows.length / rowsPerPage)
  }, [sortedFilteredRows, rowsPerPage])

  const validCurrentPage = currentPage + 1 > pageCount ? 0 : currentPage

  const renderedRows = useMemo(() => {
    const start = validCurrentPage * rowsPerPage
    const end = rowsPerPage == 0 ? sortedFilteredRows.length : validCurrentPage * rowsPerPage + rowsPerPage

    return sortedFilteredRows.slice(start, end)
  }, [sortedFilteredRows, rowsPerPage, validCurrentPage])

  const exportData = () => {
    const options: ConvertOptions = {
      openAsDownload: true,
      format: 'xlsx',
      filename: `${props.title}-${new Date().getTime()}`,
    }
    const result: string[][] = []
    const exportColumns = renderedColumns.filter((c) => c.exportFormat)
    const headerRow = exportColumns.map((c) => c.name.toString())
    result.push(headerRow)
    for (const row of renderedRows) {
      const values: string[] = []
      for (const column of exportColumns) {
        values.push(column.exportFormat!(row) ?? '')
      }
      result.push(values)
    }
    const sheet = {
      name: '1',
      from: {
        array: result,
        arrayHasHeader: true,
      },
    }
    ExcellentExport.convert(options, [sheet])
  }

  const rowClass = useCallback(
    (row: T, index: number) => {
      const classes: string[] = []
      const rowClassFn = props.rowClass
      const rowKeyFn = props.rowKeyGetter
      if (rowClassFn) {
        classes.push(rowClassFn(row, index))
      }
      if (selectedRowKey === rowKeyFn(row)) {
        classes.push('selected-row')
      }
      return classes.join(' ')
    },
    [props.rowClass, props.rowKeyGetter, selectedRowKey]
  )

  return (
    <div className="grid-component">
      <GridOptions
        title={props.title}
        icon={props.icon}
        columns={columns}
        rowsPerPage={rowsPerPage}
        filtersVisible={filtersVisible}
        onRowsPerPageChange={updateRowsPerPage}
        onColumnsChange={updateColumns}
        onFiltersVisibleChange={updateFilersVisible}
        onExport={exportData}
        onReload={props.onReload}
        defaultColumns={props.columns}
      />
      <DataGrid
        className="rdg-light"
        style={props.style}
        rowHeight={41}
        headerRowHeight={45}
        columns={renderedColumns}
        defaultColumnOptions={{ minWidth: 100, maxWidth: 500 }}
        sortColumns={sortColumns}
        rows={renderedRows}
        topSummaryRows={filtersVisible ? [filters] : []}
        onSortColumnsChange={updateSortColumns}
        onCellClick={(data) => setSelectedRowKey(props.rowKeyGetter(data.row))}
        onCellDoubleClick={props.onCellDoubleClick}
        rowClass={rowClass}
        rowKeyGetter={props.rowKeyGetter}
        renderers={{
          renderSortStatus: (p) => (
            <i
              style={{ paddingLeft: 10 }}
              className={
                p.sortDirection === 'ASC' ? 'icon-sort-up' : p.sortDirection === 'DESC' ? 'icon-sort-down' : 'icon-sort'
              }
            />
          ),
        }}
      />
      <Pagination currentPage={validCurrentPage} pageCount={pageCount} onChange={setCurrentPage} />
      <FilterModal
        column={filterModalColumn}
        onChange={updateFilter}
        onClose={() => setFilterModalColumn(undefined)}
      ></FilterModal>
    </div>
  )
}

function sort<T>(array: T[], direction: SortDirection, sortBy: (item: T) => string | number | Date | undefined) {
  const asc = direction === 'ASC' ? -1 : 1
  return array.sort((a, b) => {
    const aValue = sortBy(a)
    const bValue = sortBy(b)
    if (aValue == null) {
      return bValue == null ? 0 : -asc
    }
    if (bValue == null) {
      return aValue == null ? 0 : asc
    }
    if (aValue < bValue) {
      return asc
    }
    if (aValue > bValue) {
      return -asc
    }
    return 0
  })
}
